import React, { useCallback, useState, useMemo } from 'react';
import VideoRecorder from 'Shared/components/VideoRecorder/VideoRecorder';
import { createFormField } from 'Shared/utils/createFormField';
import WpButton from 'Wpds/components/button/WpButton';

import './VideoInput.scss';
import WpIcon from 'Wpds/components/icon/WpIcon';

type Props = {
  value: {
    audioOnly: boolean;
    file?: {
      size: number;
      type: string;
    };
    _destroy: boolean;
    url?: string;
    preview: string;
  };
  onChange: Function;
  removable?: true;
  switchButton: Function;
};

type SwitchButtonProps = {
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

const defaultSwitchButton = ({ onClick }: SwitchButtonProps) => (
  <button type="button" className="btn form-video-input__empty-state  p-4 h-100" onClick={onClick}>
    <WpIcon name="video-plus" />
    <div className="my-2">
      <b>Record a Video</b>
    </div>
    <div>You can view & re-record or delete any videos before submitting</div>
  </button>
);

function VideoInput({ onChange, value, switchButton = defaultSwitchButton }: Props) {
  const [isVideoRecorderOpen, setIsVideoRecorderOpen] = useState(false);

  const handleVideoRecorderToggle = useCallback(() => setIsVideoRecorderOpen(!isVideoRecorderOpen), [
    isVideoRecorderOpen,
  ]);

  const handleSaveVideo = useCallback(video => onChange(video), [onChange]);

  const handleRemoveVideo = useCallback(() => {
    onChange(value.url ? { ...value, _destroy: true } : null);
  }, [onChange, value]);

  const isVideoPresented = Boolean(value && (value.file || value.url) && !value._destroy);

  const videoSrc = useMemo(() => (isVideoPresented ? value.url || window.URL.createObjectURL(value.file) : null), [
    isVideoPresented,
    value.url,
    value.file,
  ]);

  return (
    <div>
      {isVideoPresented ? (
        <>
          <video controls src={videoSrc} width="100%" />
          <span className="form-video-input__button">
            <WpButton block={false} variant="tertiary" color="danger" btnType="button" onClick={handleRemoveVideo}>
              <WpIcon name="trash" />
              Delete Video
            </WpButton>
          </span>
        </>
      ) : (
        <>{switchButton({ onClick: handleVideoRecorderToggle })}</>
      )}
      {isVideoRecorderOpen && <VideoRecorder onSave={handleSaveVideo} onHide={handleVideoRecorderToggle} />}
    </div>
  );
}

export const FormVideoInput = createFormField(VideoInput);
export default VideoInput;
